'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, ClipboardList, Users, FolderOpen, Settings, ChevronLeft, ChevronRight, LogOut, Bell, Shield, Database, HardHat,  } from 'lucide-react';

type NavItem = {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: string | null;
  badgeVariant?: string;
};

type NavGroup = {
  group: string;
  items: NavItem[];
};

const NAV_BY_ROLE: Record<string, NavGroup[]> = {
  super_admin: [
    {
      group: 'Administration',
      items: [
        { label: 'Subscriber Management', href: '/super-admin', icon: Shield, badge: null },
      ],
    },
  ],
  company_admin: [
    {
      group: 'Workspace',
      items: [
        { label: 'Dashboard', href: '/', icon: LayoutDashboard, badge: null },
        { label: 'Job Cards', href: '/job-card-management', icon: ClipboardList, badge: '4', badgeVariant: 'warning' },
      ],
    },
    {
      group: 'Management',
      items: [
        { label: 'Clients', href: '/clients-management', icon: Users, badge: null },
        { label: 'Mechanics', href: '/mechanic-portal', icon: HardHat, badge: null },
        { label: 'Notifications', href: '/notifications', icon: Bell, badge: '7', badgeVariant: 'info' },
      ],
    },
    {
      group: 'Administration',
      items: [
        { label: 'Master Data', href: '/master-data', icon: Database, badge: null },
        { label: 'Settings', href: '/settings', icon: Settings, badge: null },
      ],
    },
  ],
  client: [
    {
      group: 'My Portal',
      items: [
        { label: 'My Machines & Job Cards', href: '/client-portal', icon: FolderOpen, badge: null },
      ],
    },
  ],
  mechanic: [
    {
      group: 'My Work',
      items: [
        { label: 'My Job Cards', href: '/mechanic-portal', icon: HardHat, badge: null },
      ],
    },
  ],
};

const badgeColors: Record<string, string> = {
  warning: 'bg-amber-100 text-amber-700',
  alert: 'bg-red-100 text-red-600',
  info: 'bg-blue-100 text-blue-700',
};

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  company_admin: 'Company Admin',
  client: 'Client',
  mechanic: 'Mechanic',
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const router = useRouter();

  const navItems = user ? (NAV_BY_ROLE[user.role] ?? []) : [];

  const handleLogout = () => {
    logout();
    router.push('/login');
  };

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center px-0' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-base text-foreground tracking-tight">
            JobCardFlow
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        {navItems.map((group) => (
          <div key={`group-${group.group}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-1 text-[11px] font-600 uppercase tracking-widest text-muted-foreground">
                {group.group}
              </p>
            )}
            {group.items.map((item) => {
              const active = pathname === item.href;
              return (
                <Link
                  key={`nav-${item.href}-${item.label}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative
                    ${active
                      ? 'bg-primary/10 text-primary' :'text-secondary-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                >
                  <item.icon
                    size={18}
                    className={`shrink-0 ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
                  />
                  {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span
                      className={`text-[11px] font-600 px-1.5 py-0.5 rounded-full ${
                        badgeColors[item.badgeVariant ?? 'info']
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div className="border-t border-border p-3">
        <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Shield size={14} className="text-primary" />
          </div>
          {!collapsed && user && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{roleLabels[user.role]}</p>
              {user.companyName && (
                <p className="text-[10px] text-muted-foreground/70 truncate">{user.companyName}</p>
              )}
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleLogout}
              className="p-1 rounded hover:bg-muted transition-colors"
              title="Sign out"
            >
              <LogOut size={14} className="text-muted-foreground" />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-muted transition-colors mt-1"
            title="Sign out"
          >
            <LogOut size={14} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center shadow-sm hover:bg-secondary transition-colors z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-muted-foreground" />
        ) : (
          <ChevronLeft size={12} className="text-muted-foreground" />
        )}
      </button>
    </aside>
  );
}